import {
    all,
    cancelled,
    fork,
    put,
    race,
    take,
    takeLatest
} from 'redux-saga/effects';

import { eventChannel } from 'redux-saga';

import {
    getInvitedPropertyMembersFailure,
    getInvitedPropertyMembersSuccess,
    getMemberKeysFailure,
    getMemberKeysSuccess,
    getPropertyMembersFailure,
    getPropertyMembersSuccess,
    getPropertyMembersCodesSuccess,
    getPropertyMembersCodesFailure,
    getVgPropertyByIdSuccess,
    getVgPropertyByIdFailure
} from '../actions/VirtualGuard';

import {
    GET_MEMBER_KEYS,
    GET_PROPERTY_MEMBERS,
    LOGOUT_USER,
    RESET_MEMBER_KEYS,
    RESET_PROPERTY_MEMBERS,
    GET_PROPERTY_MEMBERS_CODES,
    GET_VG_PROPERTY_BY_ID
} from '../actions/types';

import { db, fsTools } from 'config/firebase';

import { log } from '../../utils/Loggers';
import { OtcStatus } from '../../utils/constants/otc-status';

const { collection, query, where, onSnapshot } = fsTools;

const accessKeysCollectionRef = collection(db, 'access_keys');
const userCollectionRef = collection(db, 'users');
const invitesCollectionRef = collection(db, 'invites');
const otcCollectionRef = collection(db, 'otc');
const propertiesCollectionRef = collection(db, 'properties');

const ACTIVE_INVITE_STATUSES = ['pending_approval', 'invited'];

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////// Get Property Members ///////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* propertyMembersCollectionWatch({ payload }) {
    const { propertyId } = payload;
    let unsubscribePropertyMembersCollectionData;

    const propertyMembersCollectionQuery = query(
        userCollectionRef,
        where('property_ids', 'array-contains', propertyId)
    );

    const propertyMembersCollectionChannel = eventChannel(emit => {
        unsubscribePropertyMembersCollectionData = onSnapshot(
            propertyMembersCollectionQuery,
            querySnapshot => {
                const propertyMembers = [];
                querySnapshot.forEach(doc => {
                    propertyMembers.push(doc.data());
                });
                if (!querySnapshot.size) {
                    emit([]);
                } else {
                    emit(propertyMembers);
                }
            }
        );
        return unsubscribePropertyMembersCollectionData;
    });

    try {
        while (true) {
            const { userSignOut, resetPropertyMembers, propertyMembersCollectionData } =
                yield race({
                    userSignOut: take(LOGOUT_USER),
                    resetPropertyMembers: take(RESET_PROPERTY_MEMBERS),
                    propertyMembersCollectionData: take(propertyMembersCollectionChannel)
                });

            if (userSignOut || resetPropertyMembers) {
                propertyMembersCollectionChannel.close();
            } else {
                yield put(getPropertyMembersSuccess(propertyMembersCollectionData));
            }
        }
    } catch (error) {
        log('Virtual Guard Error: watching property members by property ID (FS)', {
            error,
            propertyId
        });
        yield put(getPropertyMembersFailure(error));
    } finally {
        unsubscribePropertyMembersCollectionData();
        if (yield cancelled()) {
            propertyMembersCollectionChannel.close();
            unsubscribePropertyMembersCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////// Get Property Members Codes /////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* propertyMembersCodesCollectionWatch({ payload }) {
    const { propertyId } = payload;
    let unsubscribePropertyMembersCodesCollectionData;

    const propertyMembersCodesCollectionQuery = query(
        otcCollectionRef,
        where('property_id', '==', propertyId),
        where('status', '==', OtcStatus.ACTIVE)
    );

    const propertyMembersCodesCollectionChannel = eventChannel(emit => {
        unsubscribePropertyMembersCodesCollectionData = onSnapshot(
            propertyMembersCodesCollectionQuery,
            querySnapshot => {
                const propertyMembersCodes = [];
                querySnapshot.forEach(doc => {
                    propertyMembersCodes.push(doc.data());
                });
                if (!querySnapshot.size) {
                    emit([]);
                } else {
                    emit(propertyMembersCodes);
                }
            }
        );
        return unsubscribePropertyMembersCodesCollectionData;
    });

    try {
        while (true) {
            const {
                userSignOut,
                resetPropertyMembers,
                propertyMembersCodesCollectionData
            } = yield race({
                userSignOut: take(LOGOUT_USER),
                resetPropertyMembers: take(RESET_PROPERTY_MEMBERS),
                propertyMembersCodesCollectionData: take(
                    propertyMembersCodesCollectionChannel
                )
            });

            if (userSignOut || resetPropertyMembers) {
                propertyMembersCodesCollectionChannel.close();
            } else {
                yield put(
                    getPropertyMembersCodesSuccess(propertyMembersCodesCollectionData)
                );
            }
        }
    } catch (error) {
        log('Virtual Guard Error: watching property members codes by property ID (FS)', {
            error,
            propertyId
        });
        yield put(getPropertyMembersCodesFailure(error));
    } finally {
        unsubscribePropertyMembersCodesCollectionData();
        if (yield cancelled()) {
            propertyMembersCodesCollectionChannel.close();
            unsubscribePropertyMembersCodesCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////// Get VG Property By ID //////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* vgPropertyByIdCollectionWatch({ payload }) {
    const { propertyId } = payload;
    let unsubscribePropertyByIdCollectionData;

    const propertyByIdCollectionQuery = query(
        propertiesCollectionRef,
        where('id', '==', propertyId)
    );

    const propertyByIdCollectionChannel = eventChannel(emit => {
        unsubscribePropertyByIdCollectionData = onSnapshot(
            propertyByIdCollectionQuery,
            querySnapshot => {
                const properties = [];
                querySnapshot.forEach(doc => {
                    properties.push(doc.data());
                });
                if (!querySnapshot.size) {
                    emit([]);
                } else {
                    emit(properties);
                }
            }
        );
        return unsubscribePropertyByIdCollectionData;
    });

    try {
        while (true) {
            const { userSignOut, resetPropertyMembers, propertyByIdCollectionData } =
                yield race({
                    userSignOut: take(LOGOUT_USER),
                    resetPropertyMembers: take(RESET_PROPERTY_MEMBERS),
                    propertyByIdCollectionData: take(propertyByIdCollectionChannel)
                });

            if (userSignOut || resetPropertyMembers) {
                propertyByIdCollectionChannel.close();
            } else {
                yield put(getVgPropertyByIdSuccess(propertyByIdCollectionData));
            }
        }
    } catch (error) {
        log('Virtual Guard Error: watching property by property ID (FS)', {
            error,
            propertyId
        });
        yield put(getVgPropertyByIdFailure(error));
    } finally {
        unsubscribePropertyByIdCollectionData();
        if (yield cancelled()) {
            propertyByIdCollectionChannel.close();
            unsubscribePropertyByIdCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////// Get Invited Property Members ///////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* invitedPropertyMembersCollectionWatch({ payload }) {
    const { propertyId } = payload;
    let unsubscribeInvitedPropertyMembersCollectionData;

    const invitedPropertyMembersCollectionQuery = query(
        invitesCollectionRef,
        where('property_id', '==', propertyId),
        where('status', 'in', ACTIVE_INVITE_STATUSES)
    );

    const invitedPropertyMembersCollectionChannel = eventChannel(emit => {
        unsubscribeInvitedPropertyMembersCollectionData = onSnapshot(
            invitedPropertyMembersCollectionQuery,
            querySnapshot => {
                const invitedMembers = [];
                querySnapshot.forEach(doc => {
                    invitedMembers.push(doc.data());
                });
                if (!querySnapshot.size) {
                    emit([]);
                } else {
                    emit(invitedMembers);
                }
            }
        );
        return unsubscribeInvitedPropertyMembersCollectionData;
    });

    try {
        while (true) {
            const {
                userSignOut,
                resetPropertyMembers,
                invitedPropertyMembersCollectionData
            } = yield race({
                userSignOut: take(LOGOUT_USER),
                resetPropertyMembers: take(RESET_PROPERTY_MEMBERS),
                invitedPropertyMembersCollectionData: take(
                    invitedPropertyMembersCollectionChannel
                )
            });

            if (userSignOut || resetPropertyMembers) {
                invitedPropertyMembersCollectionChannel.close();
            } else {
                yield put(
                    getInvitedPropertyMembersSuccess(invitedPropertyMembersCollectionData)
                );
            }
        }
    } catch (error) {
        log(
            'Virtual Guard Error: watching invited property members by property ID (FS)',
            {
                error,
                propertyId
            }
        );
        yield put(getInvitedPropertyMembersFailure(error));
    } finally {
        unsubscribeInvitedPropertyMembersCollectionData();
        if (yield cancelled()) {
            invitedPropertyMembersCollectionChannel.close();
            unsubscribeInvitedPropertyMembersCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////// Get Member Keys //////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* memberKeysCollectionWatch({ payload }) {
    const { propertyId, memberId } = payload;
    let unsubscribeMemberKeysCollectionData;
    const memberKeysCollectionQuery = query(
        accessKeysCollectionRef,
        where('property_id', '==', propertyId),
        where('consumer_id', '==', memberId)
    );

    const memberKeysCollectionChannel = eventChannel(emit => {
        unsubscribeMemberKeysCollectionData = onSnapshot(
            memberKeysCollectionQuery,
            querySnapshot => {
                const memberKeys = [];
                querySnapshot.forEach(doc => {
                    memberKeys.push(doc.data());
                });
                emit(memberKeys);
            }
        );

        return unsubscribeMemberKeysCollectionData;
    });

    try {
        while (true) {
            const { userSignOut, resetMemberKeys, memberKeysCollectionData } = yield race(
                {
                    userSignOut: take(LOGOUT_USER),
                    resetMemberKeys: take(RESET_MEMBER_KEYS),
                    memberKeysCollectionData: take(memberKeysCollectionChannel)
                }
            );

            if (userSignOut || resetMemberKeys) {
                memberKeysCollectionChannel.close();
            } else {
                yield put(getMemberKeysSuccess(memberKeysCollectionData));
            }
        }
    } catch (error) {
        log(
            'Virtual Guard Error: watching member access keys by uid and property ID (FS)',
            {
                error,
                propertyId,
                memberId
            }
        );
        yield put(getMemberKeysFailure(error));
    } finally {
        unsubscribeMemberKeysCollectionData();
        if (yield cancelled()) {
            memberKeysCollectionChannel.close();
            unsubscribeMemberKeysCollectionData();
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////// Action Creators For Root Saga ////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* getPropertyMembers() {
    yield takeLatest(GET_PROPERTY_MEMBERS, propertyMembersCollectionWatch);
}

export function* getPropertyMembersCodes() {
    yield takeLatest(GET_PROPERTY_MEMBERS_CODES, propertyMembersCodesCollectionWatch);
}

export function* getVgPropertyById() {
    yield takeLatest(GET_VG_PROPERTY_BY_ID, vgPropertyByIdCollectionWatch);
}

export function* getInvitedPropertyMembers() {
    yield takeLatest(GET_PROPERTY_MEMBERS, invitedPropertyMembersCollectionWatch);
}

export function* getMemberKeysCollection() {
    yield takeLatest(GET_MEMBER_KEYS, memberKeysCollectionWatch);
}

export default function* rootSaga() {
    yield all([
        fork(getPropertyMembers),
        fork(getInvitedPropertyMembers),
        fork(getMemberKeysCollection),
        fork(getPropertyMembersCodes),
        fork(getVgPropertyById)
    ]);
}
